/**
 * ImageUploadNode (Canvas V2)
 *
 * Upload a local image file. Supports file picker and drag-and-drop.
 * 10MB limit, backend API upload, image preview with object-cover.
 * FloatingNodeLabel (editable) above the node, hidden output port.
 *
 * Uses a custom card wrapper matching ImageGeneratorNode's clean,
 * image-focused card style with boxShadow for state indication.
 * No BaseNode header bar — just the image content.
 *
 * 5 visual states:
 *   1. Empty — clickable label, upload icon + "Upload Image"
 *   2. Uploading — image preview + overlay spinner
 *   3. Error — error icon + message + retry hint
 *   4. Success — clean image only
 *   5. Hover — overlay buttons (expand, download, replace)
 *
 * Output: out_image (image)
 */

import { memo, useCallback, useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { NodeProps, Position } from 'reactflow'
import { useNodeActions, FloatingNodeLabel, ASPECT_WIDTH_MAP, snapToClosestAspectWidth } from '../shared'
import { TypedHandle } from '../../TypedHandle'
import { useCanvasOptional } from '../../CanvasContext'
import { useFileUpload } from '../shared/useFileUpload'
import { api } from '../../../../api/client'
import { useProjectId } from '../../../../contexts/ProjectIdContext'

export interface ImageUploadNodeData {
  out_image?: string
  in_file_name?: string
  status?: 'idle' | 'running' | 'completed' | 'error'
  error?: string
  label?: string
  /** File object passed on node creation for auto-upload */
  initialFile?: File
  /** Eagerly-created blob URL for instant preview */
  _initialPreviewUrl?: string
}

const ImageUploadNode = memo(({ id, data, selected }: NodeProps<ImageUploadNodeData>) => {
  const {
    out_image,
    in_file_name,
    status = 'idle',
    error,
  } = data

  const canvas = useCanvasOptional()
  const projectId = useProjectId()

  // Track whether the label was manually set by the user
  const labelManuallySet = useRef(false)

  // Local state for immediate label display (Issue 2: frontend-origin YDoc
  // writes skip React state rebuild, so we need local state for instant feedback)
  const [derivedLabel, setDerivedLabel] = useState<string>('')

  const updateNodeData = useCallback(
    (updates: Record<string, unknown>) => {
      // Auto-set label from uploaded filename (if not manually renamed)
      const merged = { ...updates }
      if (updates.in_file_name && typeof updates.in_file_name === 'string' && !labelManuallySet.current) {
        const nameWithoutExt = (updates.in_file_name as string).replace(/\.[^.]+$/, '')
        merged.label = nameWithoutExt
        setDerivedLabel(nameWithoutExt)
      }
      canvas?.updateNodeData(id, merged)
    },
    [id, canvas]
  )

  const {
    previewUrl,
    isUploading,
    handleFileChange,
    dragHandlers,
    isDragOver,
    retry,
    multiFileWarning,
    uploadFile,
  } = useFileUpload({
    acceptMime: 'image',
    updateNodeData,
    persistedValue: out_image,
    initialPreviewUrl: data._initialPreviewUrl,
  })

  // Auto-upload initialFile when canvas context is ready
  const initialFileProcessed = useRef(false)
  useEffect(() => {
    if (data.initialFile && canvas && !initialFileProcessed.current) {
      initialFileProcessed.current = true
      uploadFile(data.initialFile)
    }
  }, [data.initialFile, canvas, uploadFile])

  // Clear initialFile from node data after processing to avoid re-uploading on re-render
  const initialFileCleared = useRef(false)
  useEffect(() => {
    if (data.initialFile && !initialFileCleared.current) {
      initialFileCleared.current = true
      // Use a timeout to let the upload start before clearing
      setTimeout(() => {
        canvas?.updateNodeData(id, { initialFile: undefined })
      }, 100)
    }
  }, [data.initialFile, id, canvas])

  const { handleDownload } = useNodeActions({ nodeId: id })

  // Use either the object URL preview or derive from the persisted out_image path
  const persistedUrl = out_image ? api.getAssetUrl(projectId, out_image) : undefined
  const displayUrl = previewUrl || persistedUrl

  const onDownload = useCallback(() => {
    if (displayUrl) {
      handleDownload(displayUrl, in_file_name || 'uploaded-image.jpg')
    }
  }, [displayUrl, in_file_name, handleDownload])

  // Dynamic aspect ratio from loaded image
  const [aspectRatio, setAspectRatio] = useState<string | null>(null)
  // Node width snapped to closest ASPECT_WIDTH_MAP bucket; default 16:9 = 315px
  const [nodeWidth, setNodeWidth] = useState(ASPECT_WIDTH_MAP['16:9'])

  // Lightbox state
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    if (img.naturalWidth && img.naturalHeight) {
      setAspectRatio(`${img.naturalWidth}/${img.naturalHeight}`)
      setNodeWidth(snapToClosestAspectWidth(img.naturalWidth, img.naturalHeight))
    }
  }, [])

  // Reset aspect ratio and node width when image is cleared
  useEffect(() => {
    if (!displayUrl) {
      setAspectRatio(null)
      setNodeWidth(ASPECT_WIDTH_MAP['16:9'])
    }
  }, [displayUrl])

  const handleRename = useCallback(
    (newName: string) => {
      labelManuallySet.current = true
      setDerivedLabel(newName)
      canvas?.updateNodeData(id, { label: newName })
    },
    [id, canvas]
  )

  const inputId = `file-upload-${id}`
  const isError = status === 'error'
  const hasError = isError && !!error

  return (
    <>
      <div className="relative">
        {/* Floating editable label above the card */}
        <FloatingNodeLabel
          name={derivedLabel || data.label || ''}
          fallback="Untitled"
          onRename={handleRename}
        />

        {/* Card wrapper — matches ImageGeneratorNode's boxShadow pattern */}
        <div
          className="rounded-lg overflow-hidden"
          style={{
            width: nodeWidth,
            boxShadow: selected
              ? '0 0 0 2px rgba(255,255,255,0.7)'
              : hasError
                ? '0 0 0 1px rgba(239,68,68,0.5)'
                : '0 2px 8px rgba(0,0,0,0.3)',
          }}
          {...dragHandlers}
        >
          {/* Hidden file input */}
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            id={inputId}
            disabled={isUploading}
          />

          {/* Main content area — 5 visual states */}
          {isUploading && displayUrl ? (
            /* ── State 2: Uploading — image preview + overlay spinner ── */
            <div
              className="relative"
              style={{ aspectRatio: aspectRatio || '16/9', overflow: 'hidden' }}
            >
              <img
                src={displayUrl}
                alt={in_file_name || 'Uploading image'}
                className="w-full h-full object-cover block"
                onLoad={handleImageLoad}
              />
              <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span className="text-xxs text-white/80 mt-2">Uploading image...</span>
              </div>
            </div>
          ) : hasError ? (
            /* ── State 3: Error — icon + message + retry ── */
            <div
              className="w-full bg-canvas flex flex-col items-center justify-center px-6 cursor-pointer"
              style={{ aspectRatio: '16/9' }}
              onClick={retry}
            >
              <svg
                className="w-8 h-8 text-red-400/60"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
              <span
                className="text-xxs text-red-400/80 mt-1.5 text-center line-clamp-2 cursor-help"
                title={error}
              >
                Failed to upload
              </span>
              <span className="text-xxs text-foreground/20 mt-1">Click to retry</span>
            </div>
          ) : displayUrl ? (
            /* ── State 4: Success — clean image ── */
            <div
              className="relative group"
              style={{ aspectRatio: aspectRatio || '16/9', overflow: 'hidden' }}
            >
              <img
                src={displayUrl}
                alt={in_file_name || 'Uploaded image'}
                className={`w-full h-full object-cover block transition-transform duration-300 ease-out ${
                  !selected ? 'group-hover:scale-[1.02]' : ''
                }`}
                onLoad={handleImageLoad}
              />

              {/* ── State 5: Hover overlay buttons — expand, download, replace ── */}
              <div className="absolute top-2 right-2 flex gap-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                {/* Expand */}
                <button
                  onClick={(e) => { e.stopPropagation(); setIsModalOpen(true) }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                  title="Expand"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4" />
                  </svg>
                </button>
                {/* Download */}
                <button
                  onClick={(e) => { e.stopPropagation(); onDownload() }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                  title="Download"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </button>
                {/* Replace — label triggers file picker */}
                <label
                  htmlFor={inputId}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors cursor-pointer nodrag"
                  title="Replace"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </label>
              </div>
            </div>
          ) : (
            /* ── State 1: Empty — clickable upload area ── */
            <label
              htmlFor={inputId}
              className={`w-full bg-canvas flex flex-col items-center justify-center cursor-pointer transition-colors ${
                isDragOver
                  ? 'bg-primary/5 ring-2 ring-primary/40 ring-inset'
                  : ''
              }`}
              style={{ aspectRatio: '16/9' }}
            >
              {isDragOver ? (
                <span className="text-xs text-primary">Drop to upload</span>
              ) : (
                <>
                  <svg
                    className="w-8 h-8 text-foreground/15"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                    />
                  </svg>
                  <span className="text-xxs text-foreground/30 mt-1">Upload Image</span>
                </>
              )}
            </label>
          )}

          {/* Multi-file drop warning */}
          {multiFileWarning && (
            <div className="px-2 py-1.5 bg-amber-500/10 border-t border-amber-500/30">
              <span className="text-xxs text-amber-400">{multiFileWarning}</span>
            </div>
          )}
        </div>

        {/* Output handle: out_image — visually hidden, kept in DOM for edges */}
        <TypedHandle
          type="source"
          position={Position.Right}
          id="out_image"
          portType="image"
          style={{ top: '50%', width: 0, height: 0, border: 'none', background: 'transparent', minWidth: 0, minHeight: 0 }}
        />
      </div>

      {/* Fullscreen Lightbox */}
      {isModalOpen && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
            >
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={displayUrl}
              alt="Full size"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>,
        document.body
      )}
    </>
  )
})

ImageUploadNode.displayName = 'ImageUploadNode'
export default ImageUploadNode
